import { __rest } from 'tslib';
import React__default, { createElement, useContext, Component, createRef } from 'react';
import { StyleSheet, Text, View, TouchableOpacity, TouchableWithoutFeedback, Platform, TextInput } from 'react-native';

const styles = StyleSheet.create({
    baseText: {
        marginBottom: 10,
    },
    text: {
        fontSize: 16,
    },
    h1: {
        fontSize: 30,
    },
    h2: {
        fontSize: 28,
    },
    h3: {
        fontSize: 26,
    },
    h4: {
        fontSize: 24,
    },
    h5: {
        fontSize: 22,
    },
    h6: {
        fontSize: 20,
    },
});
const SilkText = (_a) => {
    var { style } = _a, props = __rest(_a, ["style"]);
    return (createElement(Text, Object.assign({ style: [styles.text, style] }, props)));
};
const Heading1 = (_a) => {
    var { style } = _a, props = __rest(_a, ["style"]);
    return createElement(Text, Object.assign({ style: [styles.baseText, styles.h1, style] }, props));
};
const Heading2 = (_a) => {
    var { style } = _a, props = __rest(_a, ["style"]);
    return createElement(Text, Object.assign({ style: [styles.baseText, styles.h2, style] }, props));
};
const Heading3 = (_a) => {
    var { style } = _a, props = __rest(_a, ["style"]);
    return createElement(Text, Object.assign({ style: [styles.baseText, styles.h3, style] }, props));
};
const Heading4 = (_a) => {
    var { style } = _a, props = __rest(_a, ["style"]);
    return createElement(Text, Object.assign({ style: [styles.baseText, styles.h4, style] }, props));
};
const Heading5 = (_a) => {
    var { style } = _a, props = __rest(_a, ["style"]);
    return createElement(Text, Object.assign({ style: [styles.baseText, styles.h5, style] }, props));
};
const Heading6 = (_a) => {
    var { style } = _a, props = __rest(_a, ["style"]);
    return createElement(Text, Object.assign({ style: [styles.baseText, styles.h6, style] }, props));
};

let IconComponent = (props) => {
    return (React__default.createElement(View, null,
        React__default.createElement(Text, null, props.name)));
};
function installIconComponent(iconComponent) {
    IconComponent = iconComponent;
}
const Icon = (props) => {
    return React__default.createElement(IconComponent, Object.assign({}, props));
};
Icon.defaultProps = {
    size: 24,
    color: '#666',
};

const initialValue = {
    colors: {
        primary: '#007bff',
        secondary: '#6c757d',
        success: '#28a745',
        danger: '#dc3545',
        warning: '#ffc107',
        info: '#17a2b8',
        light: '#f8f9fa',
        dark: '#343a40',
        transparent: 'transparent',
        white: '#FFF',
        black: '#555',
        gray: '#a6a6a6',
        smokeWhite: '#f4f4f4',
    },
};
const Colors = initialValue.colors;
const ThemeContext = React__default.createContext(initialValue);
const ThemeConsumer = ThemeContext.Consumer;
function useTheme() {
    return useContext(ThemeContext);
}

function variantToColor(variant, colors) {
    let themeColor;
    switch (variant) {
        case 'primary':
            themeColor = colors.primary;
            break;
        case 'secondary':
            themeColor = colors.secondary;
            break;
        case 'success':
            themeColor = colors.success;
            break;
        case 'danger':
            themeColor = colors.danger;
            break;
        case 'warning':
            themeColor = colors.warning;
            break;
        case 'info':
            themeColor = colors.info;
            break;
        case 'light':
            themeColor = colors.light;
            break;
        case 'dark':
            themeColor = colors.dark;
            break;
        case 'link':
        default:
            themeColor = colors.transparent;
            break;
    }
    return themeColor;
}
const lightVariants = ['light', 'link'];
const isLightVariant = (variant) => !~lightVariants.indexOf(variant);

const styles$1 = StyleSheet.create({
    containerNatural: {
        alignSelf: 'flex-start',
    },
    button: {
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
        paddingVertical: 10,
        paddingHorizontal: 15,
    },
    alignLeft: {
        justifyContent: 'flex-start',
    },
    iconRight: {
        marginLeft: 5,
    },
    iconLeft: {
        marginRight: 5,
    },
    disabledButton: {
        opacity: 0.6,
    },
    buttonBorder: {
        borderWidth: 1,
    },
    hasRightIcon: {
        flexDirection: 'row-reverse',
    },
    iconStyle: {
        alignItems: 'center',
        justifyContent: 'center',
    },
});
const Button = ({ children, onPress, variant, disabled, block, ghost, icon, iconPosition, style, iconStyle, textStyle, iconSize, }) => {
    const theme = useTheme();
    const buttonStyles = [styles$1.buttonBorder];
    const themeColor = variantToColor(variant, theme.colors);
    if (variant === 'link') {
        buttonStyles.push({
            borderWidth: 0,
        });
    }
    else {
        if (ghost) {
            buttonStyles.push({
                borderColor: themeColor,
            });
        }
        else {
            buttonStyles.push({
                backgroundColor: themeColor,
                borderColor: themeColor,
            });
        }
    }
    if (!block) {
        buttonStyles.push(styles$1.containerNatural);
    }
    const textStyles = [];
    const isLight = isLightVariant(variant);
    if (ghost && isLight) {
        textStyles.push({
            color: themeColor,
        });
    }
    else {
        textStyles.push({
            color: isLight ? theme.colors.white : theme.colors.black,
        });
    }
    if (disabled) {
        buttonStyles.push(styles$1.disabledButton);
    }
    if (icon) {
        buttonStyles.push(styles$1.alignLeft);
    }
    const iconStyles = [styles$1.iconStyle];
    if (iconStyle) {
        iconStyles.push(iconStyle);
    }
    if (iconPosition === 'left') {
        iconStyles.push(styles$1.iconLeft);
    }
    else if (iconPosition === 'right') {
        buttonStyles.push(styles$1.hasRightIcon);
        iconStyles.push(styles$1.iconRight);
    }
    if (style) {
        buttonStyles.push(style);
    }
    if (textStyle) {
        textStyles.push(textStyle);
    }
    let innerNode;
    if (typeof children === 'string') {
        innerNode = React__default.createElement(Text, { style: textStyles }, children);
    }
    else {
        innerNode = children;
    }
    const node = (React__default.createElement(React__default.Fragment, null,
        icon && (React__default.createElement(View, { style: iconStyles },
            React__default.createElement(Icon, { name: icon, color: isLight ? theme.colors.white : theme.colors.black, size: iconSize }))),
        innerNode));
    if (onPress && !disabled) {
        return (React__default.createElement(TouchableOpacity, { onPress: onPress, style: [styles$1.button, ...buttonStyles] }, node));
    }
    return React__default.createElement(View, { style: [styles$1.button, ...buttonStyles] }, node);
};
Button.defaultProps = {
    variant: 'primary',
    ghost: false,
    disabled: false,
    block: false,
    iconPosition: 'left',
    iconSize: 14,
};

const styles$2 = StyleSheet.create({
    container: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
});
const Hitbox = (props) => (React__default.createElement(TouchableWithoutFeedback, { onPress: props.onPress },
    React__default.createElement(View, { style: styles$2.container })));

const styles$3 = StyleSheet.create({
    container: {
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,.8)',
    },
    modal: {
        backgroundColor: '#FFF',
    },
    body: {
        padding: 15,
    },
});
const Modal = ({ toggle, isOpen, children, style }) => {
    if (!isOpen) {
        return null;
    }
    if (Platform.OS !== 'web') {
        throw new Error('This component does not support modal on this native platform yet');
    }
    return (createElement("div", { style: {
            position: 'fixed',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 2,
        } },
        createElement(View, { style: styles$3.container },
            createElement(Hitbox, { onPress: toggle }),
            createElement(View, { style: [styles$3.modal, style] },
                createElement(View, { style: styles$3.body }, children)))));
};

const styles$4 = StyleSheet.create({
    container: {
        marginBottom: 10,
    },
    label: {
        marginBottom: 5,
    },
    textInput: {
        borderWidth: 1,
        paddingVertical: 5,
        paddingHorizontal: 8,
    },
    disabledView: {
        paddingVertical: 5,
        paddingHorizontal: 8,
        borderWidth: 1,
        height: 33,
    },
});
const Input = React__default.forwardRef((_a, _ref) => {
    var { label, placeholder, errorMessage, disabled, onChanged, value, style, inputStyle } = _a, props = __rest(_a, ["label", "placeholder", "errorMessage", "disabled", "onChanged", "value", "style", "inputStyle"]);
    const theme = useTheme();
    const inputStyles = [
        {
            borderColor: theme.colors.gray,
        },
    ];
    const errorBorderStyle = {
        borderColor: theme.colors.danger,
    };
    const errorMessageStyle = {
        color: theme.colors.danger,
        fontSize: 14,
    };
    if (errorMessage) {
        inputStyles.push(errorBorderStyle);
    }
    if (inputStyle) {
        inputStyles.push(inputStyle);
    }
    const _defaultRef = React__default.useRef(null);
    const ref = _ref || _defaultRef;
    return (React__default.createElement(View, { style: [styles$4.container, style] },
        label && React__default.createElement(SilkText, { style: styles$4.label }, label),
        disabled ? (React__default.createElement(View, { style: [
                styles$4.disabledView,
                ...inputStyles,
                {
                    backgroundColor: theme.colors.smokeWhite,
                    borderColor: theme.colors.smokeWhite,
                },
            ] },
            React__default.createElement(SilkText, null, value || placeholder))) : (React__default.createElement(TextInput, Object.assign({ ref: ref, editable: true, style: [styles$4.textInput, ...inputStyles], underlineColorAndroid: "transparent", placeholder: placeholder, onChangeText: onChanged, value: value }, props))),
        errorMessage && React__default.createElement(SilkText, { style: errorMessageStyle }, errorMessage)));
});
Input.defaultProps = {
    disabled: false,
};

const INDICATOR_SIZE = 20;
const styles$5 = StyleSheet.create({
    container: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 5,
    },
    indicator: {
        width: INDICATOR_SIZE,
        height: INDICATOR_SIZE,
        borderWidth: 1,
        marginRight: 5,
    },
    circleIndicator: {
        borderRadius: INDICATOR_SIZE / 2,
    },
});
const Checkbox = ({ disabled, onChange, checked, children, variant, square, }) => {
    const theme = useTheme();
    const indicatorStyles = [styles$5.indicator];
    const textStyles = [];
    if (!square) {
        indicatorStyles.push(styles$5.circleIndicator);
    }
    if (disabled) {
        textStyles.push({
            color: theme.colors.gray,
        });
        if (checked) {
            indicatorStyles.push({
                backgroundColor: theme.colors.gray,
                borderColor: theme.colors.gray,
            });
        }
        else {
            indicatorStyles.push({
                borderColor: theme.colors.gray,
            });
        }
    }
    else {
        const themeColor = variantToColor(variant, theme.colors);
        if (checked) {
            indicatorStyles.push({
                backgroundColor: themeColor,
                borderColor: themeColor,
            });
        }
        else {
            indicatorStyles.push({
                borderColor: themeColor,
            });
        }
    }
    let node = children;
    if (typeof children === 'string') {
        node = React__default.createElement(SilkText, { style: textStyles }, children);
    }
    if (disabled) {
        return (React__default.createElement(View, { style: styles$5.container },
            React__default.createElement(View, { style: indicatorStyles }),
            node));
    }
    return (React__default.createElement(TouchableOpacity, { style: styles$5.container, onPress: onChange },
        React__default.createElement(View, { style: indicatorStyles }),
        node));
};
Checkbox.defaultProps = {
    disabled: false,
    variant: 'primary',
    checked: false,
    square: true,
};

const styles$6 = StyleSheet.create({
    dropdown: {
        position: 'absolute',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: '#f4f4f4',
        backgroundColor: '#FFFFFF',
    },
    upZ: {
        zIndex: 5,
    },
});
class Dropdown extends Component {
    constructor(props) {
        super(props);
        this.state = {
            open: false,
            height: 0,
            pageX: 0,
            pageY: 0,
        };
        this._view = createRef();
        this.toggle = () => {
            this.setState(prevState => ({
                open: !prevState.open,
            }));
        };
    }
    componentDidMount() {
        this._view.current.measure((x, y, width, height, pageX, pageY) => {
            this.setState({
                height,
                pageX,
                pageY,
            });
        });
    }
    render() {
        const dropdownStyles = [styles$6.dropdown];
        const containerStyles = [];
        if (this.state.open) {
            dropdownStyles.push({
                top: this.state.height + this.state.pageY,
                left: this.state.pageX,
            });
            containerStyles.push(styles$6.upZ);
        }
        // TODO: Update legacy ref, and support mobile
        return (createElement(View, { ref: this._view, style: containerStyles },
            this.props.renderButton({
                isOpen: this.state.open,
                toggle: this.toggle,
            }),
            this.state.open && (createElement("div", { style: {
                    position: 'fixed',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 10,
                } },
                createElement(Hitbox, { onPress: this.toggle }),
                createElement(View, { style: dropdownStyles }, this.props.children)))));
    }
}

const IconMap = {
    ArrowDown: 'angle-down',
    ArrowUp: 'angle-up',
    ArrowRight: 'angle-right',
    ArrowLeft: 'angle-left',
    CircleO: 'circle-o',
};

const DropdownButton = (_a) => {
    var { isOpen, toggle, children } = _a, props = __rest(_a, ["isOpen", "toggle", "children"]);
    return (createElement(Button, Object.assign({ onPress: toggle, icon: isOpen ? IconMap.ArrowUp : IconMap.ArrowDown }, props), children));
};

const styles$7 = StyleSheet.create({
    container: {
        marginBottom: 10,
    },
    label: {
        marginBottom: 5,
    },
});
const Choices = ({ label, errorMessage, onChanged, value, style, choices, mutiple, }) => {
    const theme = useTheme();
    const errorMessageStyle = {
        color: theme.colors.danger,
        fontSize: 14,
    };
    const isChecked = (key) => {
        if (!value) {
            return false;
        }
        const selectedKeys = value.split(',');
        return !!~selectedKeys.indexOf(key);
    };
    const onCheckChange = (key) => {
        if (onChanged) {
            if (value && mutiple) {
                const selectedKeys = value.split(',');
                const newSelectedKeys = selectedKeys.filter(currentKey => currentKey !== key);
                if (newSelectedKeys.length < selectedKeys.length) {
                    onChanged(newSelectedKeys.join(','));
                }
                else if (newSelectedKeys.length === selectedKeys.length) {
                    onChanged([...selectedKeys, key].join(','));
                }
            }
            else {
                onChanged(key);
            }
        }
    };
    return (React__default.createElement(View, { style: [styles$7.container, style] },
        label && React__default.createElement(SilkText, { style: styles$7.label }, label),
        choices.map(choice => (React__default.createElement(Checkbox, { key: choice.key, square: mutiple, checked: isChecked(choice.key), onChange: () => onCheckChange(choice.key) }, choice.value))),
        errorMessage && React__default.createElement(SilkText, { style: errorMessageStyle }, errorMessage)));
};
Choices.defaultProps = {
    disabled: false,
    choices: [],
    mutiple: false,
    value: '',
};

const DropdownItem = ({ children, icon }) => (createElement(Button, { variant: "link", icon: icon, iconPosition: "left" }, children));

const styles$8 = StyleSheet.create({
    container: {
        flexDirection: 'row',
    },
    input: {
        marginRight: 5,
        marginBottom: 0,
    },
    inputStyle: {
        textAlign: 'center',
        height: 40,
        width: 40,
    },
});
class InputPin extends React__default.Component {
    constructor(props) {
        super(props);
        const refs = [];
        let value = '';
        for (let i = 0; i < Number(props.length); i += 1) {
            refs.push(React__default.createRef());
            value += ' ';
        }
        this.localRefs = refs;
        this.state = {
            value,
        };
        this.onChange = (e, index) => {
            const text = e.nativeEvent.text || ' ';
            this.setState(prevState => {
                const newValue = `${prevState.value.substr(0, index)}${text[text.length - 1]}${prevState.value.substr(index + 1)}`;
                return {
                    value: newValue,
                };
            }, () => {
                if (this.props.onChanged) {
                    this.props.onChanged(this.state.value);
                }
            });
            if (e.nativeEvent.inputType === 'deleteContentBackward') {
                if (this.localRefs[index - 1]) {
                    const node = this.localRefs[index - 1].current;
                    if (node && node.focus) {
                        node.focus();
                    }
                }
            }
            else if (!!text.trim() && this.localRefs[index + 1]) {
                const node = this.localRefs[index + 1].current;
                if (node && node.focus) {
                    node.focus();
                }
            }
        };
    }
    render() {
        const fieldStyles = this.props.fieldStyles || {};
        return (React__default.createElement(ThemeConsumer, null, theme => (React__default.createElement(View, { style: styles$4.container },
            this.props.label && (React__default.createElement(SilkText, { style: styles$4.label }, this.props.label)),
            React__default.createElement(View, { style: styles$8.container }, this.localRefs.map((ref, index) => (React__default.createElement(Input, { ref: ref, key: index, selectTextOnFocus: true, style: [styles$8.input, fieldStyles.style], inputStyle: [styles$8.inputStyle, fieldStyles.inputStyle], onChange: e => this.onChange(e, index), value: this.state.value[index] || '' })))),
            this.props.errorMessage && (React__default.createElement(SilkText, { style: {
                    color: theme.colors.danger,
                    fontSize: 14,
                } }, this.props.errorMessage))))));
    }
}
InputPin.defaultProps = {
    length: 6,
};

var index = {
    Choices,
    Text: SilkText,
    Heading1,
    Heading2,
    Heading3,
    Heading4,
    Heading5,
    Heading6,
    Button,
    Colors,
    Modal,
    Input,
    Checkbox,
    Icon,
    installIconComponent,
    Dropdown,
    DropdownButton,
    DropdownItem,
    InputPin,
    ThemeContext,
};

export default index;
export { Choices, SilkText as Text, Heading1, Heading2, Heading3, Heading4, Heading5, Heading6, Button, Colors, Modal, Input, Checkbox, Icon, installIconComponent, Dropdown, DropdownButton, DropdownItem, InputPin, ThemeContext };
